import { useUserContext } from "../../providers/UserProvider";
import { useSession } from "../useSession/useSession";
import { useOrganization } from "../useOrganization/useOrganization";
import { useLocation } from "react-router-dom";
import { useCallback, useMemo } from "react";
import {
    ANALYTICS_FLOWS,
    ANALYTICS_TRACKED_COMPONENTS,
    ANALYTICS_PRODUCT_NAME,
    ANALYTICS_SUBPRODUCT_NAME
} from "../../constants/analytics-constants";
import { GUID_PATTERN } from "../../constants/constants";

export type TrackingComponent = (typeof ANALYTICS_TRACKED_COMPONENTS)[keyof typeof ANALYTICS_TRACKED_COMPONENTS];

export type TrackingFlow = (typeof ANALYTICS_FLOWS)[keyof typeof ANALYTICS_FLOWS];

type TrackingData = {
    productName: string;
    subProductName: string;
    userId: string;
    groupId: string;
    isOptiUser: boolean;
    component: TrackingComponent;
    flow: TrackingFlow;
    path: string;
};

export const useAnalyticsTracking = () => {
    const { organizationId, organizationName, currentInstance, profile } = useUserContext();
    const { user } = useSession();
    const { organization, getOrganization } = useOrganization({ id: organizationId });
    const location = useLocation();

    const isOptiUser = useMemo(() => {
        return (
            user?.email?.toLowerCase().includes("@optimizely.com") ||
            user?.email?.toLowerCase().includes("@episerver.net") ||
            false
        );
    }, [user?.email]);

    const sendPageEvent = useCallback(() => {
        if (currentInstance?.productName && location?.pathname && organization?.crmId) {
            // Pathname already excludes query string and hash
            const cleanPath = location.pathname;

            // Remove GUIDs from the path while preserving the URL structure
            // Split by '/', filter out GUID segments, then rejoin
            const normalizedPath = cleanPath
                .split("/")
                .filter((segment) => !GUID_PATTERN.test(segment))
                .join("/");

            window.analytics.page(normalizedPath, {
                groupId: organization?.crmId,
                subProductName: currentInstance?.productName,
                productName: ANALYTICS_PRODUCT_NAME,
                userId: user?.email || profile?.email
            });
        }
    }, [organization?.crmId, location?.pathname, currentInstance?.productName, user?.email, profile?.email]);

    const sendIdentifyEvent = useCallback(() => {
        if ((user?.email || profile) && organization?.crmId && currentInstance) {
            window.analytics.identify(user?.email || profile?.email, {
                userId: user?.email || profile?.email,
                groupId: organization?.crmId,
                organizationId,
                instanceId: currentInstance.groupId,
                // in the identify call, instanceName is interpreted as the organization name
                // confirmed by Lee and Scott
                instanceName: organizationName,
                name: `${profile?.firstName} ${profile?.lastName}`,
                isOptiUser,
                // do we need groupName if instanceName holds the same value?
                groupName: organizationName
            });
        }
    }, [user?.email, currentInstance, isOptiUser, organization?.crmId, organizationId, profile, organizationName]);

    const sendGroupEvent = useCallback(() => {
        if (organization?.crmId && currentInstance) {
            window.analytics.group(organization?.crmId, {
                groupId: organization?.crmId,
                organizationId,
                instanceId: currentInstance.groupId,
                instanceName: currentInstance.instanceName,
                name: organizationName,
                hasTurnstile: true
            });
        }
    }, [currentInstance, organization?.crmId, organizationId, organizationName]);

    const sendTrackEvent = useCallback(
        ({
            name,
            component,
            flow,
            data
        }: {
            name: string;
            component: TrackingComponent;
            flow: TrackingFlow;
            data: any;
        }) => {
            const trackingData: TrackingData = {
                productName: ANALYTICS_PRODUCT_NAME,
                subProductName: ANALYTICS_SUBPRODUCT_NAME,
                userId: user?.email || profile?.email,
                groupId: organization?.crmId,
                isOptiUser,
                component,
                flow,
                path: location.pathname,
                ...data
            };

            window.analytics.track(name, trackingData);
        },
        [isOptiUser, location.pathname, organization?.crmId, profile?.email, user?.email]
    );

    const sendSessionEvent = useCallback(
        (name: string, email: string, organizationId: string) => {
            if (email && organizationId) {
                getOrganization({ id: organizationId })?.then((org) => {
                    sendTrackEvent({
                        name,
                        component: ANALYTICS_TRACKED_COMPONENTS.SESSION,
                        flow: ANALYTICS_FLOWS.LOGIN,
                        data: { userId: email, groupId: org?.crmId }
                    });
                });
            }
        },
        [getOrganization, sendTrackEvent]
    );

    return {
        sendTrackEvent,
        sendGroupEvent,
        sendIdentifyEvent,
        sendPageEvent,
        sendSessionEvent
    };
};
